// Deterministic game rules. No DOM, audio, network or wall-clock dependencies.
export type Vec = { x: number; y: number };
export type Level = {
  id: string; name: string; width: number; height: number; playerStart: Vec; data: number[];
  theme?: 'foundry' | 'bunker' | 'crypt' | 'reactor' | 'hangar';
  description?: string; playerAngle?: number; wallMaterials?: number[];
  lights?: Array<Vec & { color: string; radius: number }>;
  supplies?: Array<Vec & { kind: 'health' | 'ammo' | 'armor' }>;
};
export type WeaponId = 'pistol' | 'shotgun' | 'gatling';
export type EnemyKind = 'grunt' | 'runner' | 'spitter' | 'brute' | 'boss';
export type Difficulty = 'easy' | 'normal' | 'hard';
export type Enemy = Vec & {
  id: number; kind: EnemyKind; hp: number; maxHp: number; radius: number; speed: number; attack: number; flash: number;
  telegraph?: number; telegraphMax?: number; attackKind?: 'melee' | 'shot' | 'charge';
  aimAngle?: number; charging?: number; vulnerable?: number;
};
export type Pickup = Vec & { kind: 'health' | 'ammo' | 'armor'; life: number };
export type Bullet = Vec & { vx: number; vy: number; life: number };
export type Impact = Vec & { kind: 'wall' | 'hit'; life: number; maxLife: number };
export type Corpse = Vec & { kind: EnemyKind; life: number; maxLife: number };
export type Controls = { forward: number; strafe: number; turn: number; sprint: boolean; fire: boolean };
export const WEAPONS = {
  pistol: { name: 'Pistolet', damage: 34, cooldown: .29, range: 24, pellets: 1, spread: 0 },
  shotgun: { name: 'Fusil à pompe', damage: 23, cooldown: .78, range: 11, pellets: 7, spread: .23 },
  gatling: { name: 'Gatling', damage: 18, cooldown: .085, range: 20, pellets: 1, spread: .04 },
} as const;
export const ENEMY_STATS = {
  grunt: { hp: 68, speed: .85, radius: .27 },
  runner: { hp: 38, speed: 1.55, radius: .22 },
  spitter: { hp: 75, speed: .65, radius: .27 },
  brute: { hp: 180, speed: .6, radius: .34 },
  boss: { hp: 650, speed: .65, radius: .38 },
} as const;
export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
export function seededRandom(seed: number): () => number {
  return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t ^= t + Math.imul(t ^ t >>> 7, 61 | t); return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}
export function validLevel(value: unknown): value is Level {
  const m = value as Level;
  return !!m && typeof m.id === 'string' && typeof m.name === 'string'
    && Number.isInteger(m.width) && Number.isInteger(m.height) && m.width >= 5 && m.height >= 5 && m.width <= 128 && m.height <= 128
    && Array.isArray(m.data) && m.data.length === m.width * m.height && m.data.every(v => Number.isInteger(v) && v >= 0 && v <= 3)
    && !!m.playerStart && Number.isFinite(m.playerStart.x) && Number.isFinite(m.playerStart.y)
    && m.playerStart.x >= 1 && m.playerStart.y >= 1 && m.playerStart.x < m.width - 1 && m.playerStart.y < m.height - 1
    && canOccupy(m, m.playerStart.x, m.playerStart.y, .22)
    && (m.theme === undefined || ['foundry', 'bunker', 'crypt', 'reactor', 'hangar'].includes(m.theme))
    && (m.description === undefined || typeof m.description === 'string' && m.description.length <= 500)
    && (m.playerAngle === undefined || Number.isFinite(m.playerAngle) && Math.abs(m.playerAngle) <= Math.PI * 2)
    && (m.wallMaterials === undefined || Array.isArray(m.wallMaterials) && m.wallMaterials.length === m.data.length && m.wallMaterials.every(v => Number.isInteger(v) && v >= 0 && v <= 2))
    && (m.lights === undefined || Array.isArray(m.lights) && m.lights.length <= 128 && m.lights.every(light =>
      !!light && Number.isFinite(light.x) && Number.isFinite(light.y) && light.x >= 0 && light.y >= 0 && light.x < m.width && light.y < m.height
      && typeof light.color === 'string' && /^#[\da-f]{6}$/i.test(light.color) && Number.isFinite(light.radius) && light.radius > 0 && light.radius <= 20))
    && (m.supplies === undefined || Array.isArray(m.supplies) && m.supplies.length <= 64 && m.supplies.every(supply =>
      !!supply && Number.isFinite(supply.x) && Number.isFinite(supply.y) && ['health', 'ammo', 'armor'].includes(supply.kind)
      && supply.x >= 1 && supply.y >= 1 && supply.x < m.width - 1 && supply.y < m.height - 1 && canOccupy(m, supply.x, supply.y, .16)));
}
export function tile(level: Level, x: number, y: number): number {
  const ix = Math.floor(x), iy = Math.floor(y);
  return ix < 0 || iy < 0 || ix >= level.width || iy >= level.height ? 1 : level.data[iy * level.width + ix];
}
export function canOccupy(level: Level, x: number, y: number, radius = .22): boolean {
  for (let ty = Math.floor(y - radius); ty <= Math.floor(y + radius); ty++) {
    for (let tx = Math.floor(x - radius); tx <= Math.floor(x + radius); tx++) {
      if (tile(level, tx, ty) === 0) continue;
      const dx = x - clamp(x, tx, tx + 1), dy = y - clamp(y, ty, ty + 1);
      if (dx * dx + dy * dy < radius * radius) return false;
    }
  }
  return true;
}
export function move(level: Level, actor: Vec, dx: number, dy: number, radius = .22): void {
  // Substeps prevent tunnelling when sprinting or when a frame is delayed.
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / .12));
  for (let i = 0; i < steps; i++) {
    if (canOccupy(level, actor.x + dx / steps, actor.y, radius)) actor.x += dx / steps;
    if (canOccupy(level, actor.x, actor.y + dy / steps, radius)) actor.y += dy / steps;
  }
}
export function raycast(level: Level, origin: Vec, dx: number, dy: number) {
  if (!Number.isFinite(dx + dy) || Math.abs(dx) + Math.abs(dy) < 1e-10) return { distance: Infinity, x: 0, y: 0, side: 0, u: 0, tile: 1 };
  let x = Math.floor(origin.x), y = Math.floor(origin.y), side = 0;
  const stepX = dx < 0 ? -1 : 1, stepY = dy < 0 ? -1 : 1;
  const deltaX = dx === 0 ? Infinity : Math.abs(1 / dx), deltaY = dy === 0 ? Infinity : Math.abs(1 / dy);
  let nextX = dx === 0 ? Infinity : (dx < 0 ? origin.x - x : x + 1 - origin.x) * deltaX;
  let nextY = dy === 0 ? Infinity : (dy < 0 ? origin.y - y : y + 1 - origin.y) * deltaY;
  for (let i = 0; i < level.width + level.height + 4; i++) {
    if (nextX < nextY) { nextX += deltaX; x += stepX; side = 0; }
    else { nextY += deltaY; y += stepY; side = 1; }
    if (tile(level, x, y) !== 0) break;
  }
  const distance = Math.max(.0001, side === 0 ? nextX - deltaX : nextY - deltaY);
  const wall = side === 0 ? origin.y + distance * dy : origin.x + distance * dx;
  return { distance, x, y, side, u: wall - Math.floor(wall), tile: tile(level, x, y) };
}
export function flowField(level: Level, target: Vec): Int32Array {
  const distances = new Int32Array(level.data.length).fill(-1);
  const start = Math.floor(target.y) * level.width + Math.floor(target.x);
  if (tile(level, target.x, target.y) !== 0) return distances;
  const queue = [start]; distances[start] = 0;
  for (let head = 0; head < queue.length; head++) {
    const index = queue[head], x = index % level.width, y = Math.floor(index / level.width);
    for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]]) {
      const n = ny * level.width + nx;
      if (tile(level, nx, ny) === 0 && distances[n] === -1) { distances[n] = distances[index] + 1; queue.push(n); }
    }
  }
  return distances;
}
export function chooseHit(level: Level, origin: Vec, angle: number, enemies: Enemy[], range: number): Enemy | undefined {
  const dx = Math.cos(angle), dy = Math.sin(angle);
  let nearest = Math.min(range, raycast(level, origin, dx, dy).distance);
  let hit: Enemy | undefined;
  for (const enemy of enemies) {
    if (enemy.hp <= 0) continue;
    const ex = enemy.x - origin.x, ey = enemy.y - origin.y;
    const along = ex * dx + ey * dy, across = ex * dy - ey * dx;
    if (along <= 0 || Math.abs(across) > enemy.radius) continue;
    const entry = Math.max(0, along - Math.sqrt(enemy.radius ** 2 - across ** 2));
    if (entry < nearest) { nearest = entry; hit = enemy; }
  }
  return hit;
}

function clearPassage(level: Level, origin: Vec, target: Vec, radius: number): boolean {
  if (!canOccupy(level, target.x, target.y, radius)) return false;
  const distance = Math.hypot(target.x - origin.x, target.y - origin.y);
  if (distance < .001) return true;
  const dx = (target.x - origin.x) / distance, dy = (target.y - origin.y) / distance;
  // A visible destination need not leave room for the actor's shoulders at a corner.
  // Sweep both sides as well as the centre of its collision circle.
  for (const offset of [-radius, 0, radius]) {
    const start = { x: origin.x - dy * offset, y: origin.y + dx * offset };
    if (tile(level, start.x, start.y) !== 0 || raycast(level, start, dx, dy).distance < distance - .001) return false;
  }
  return true;
}

export class Simulation {
  level: Level;
  player: Vec & { angle: number; health: number; armor: number; stamina: number };
  enemies: Enemy[] = [];
  pickups: Pickup[] = [];
  bullets: Bullet[] = [];
  impacts: Impact[] = [];
  corpses: Corpse[] = [];
  weapon: WeaponId = 'pistol';
  gatlingSpin = 0;
  waveLabel = 'Premier contact';
  ammo = { shotgun: 14, gatling: 80 };
  wave = 1; kills = 0; shots = 0; hits = 0; time = 0;
  cooldown = 0; hurt = 0; recoil = 0; hitMarker = 0; killMarker = 0;
  damageBonus = 1; speedBonus = 1;
  phase: 'playing' | 'intermission' | 'upgrade' | 'dead' = 'playing';
  countdown = 0;
  events: string[] = [];
  explored: Uint8Array;
  private flow: Int32Array;
  private flowTimer = 0;
  private nextId = 1;
  private readonly random: () => number;
  readonly difficulty: Difficulty;
  constructor(level: Level, difficulty: Difficulty = 'normal', seed = Date.now()) {
    if (!validLevel(level)) throw new Error('Carte invalide');
    this.level = { ...level, playerStart: { ...level.playerStart }, data: [...level.data],
      ...(level.wallMaterials ? { wallMaterials: [...level.wallMaterials] } : {}),
      ...(level.lights ? { lights: level.lights.map(light => ({ ...light })) } : {}),
      ...(level.supplies ? { supplies: level.supplies.map(supply => ({ ...supply })) } : {}),
    };
    this.player = { ...level.playerStart, angle: 0, health: 100, armor: 25, stamina: 100 };
    this.player.angle = level.playerAngle ?? [0, Math.PI / 2, Math.PI, -Math.PI / 2].sort((a, b) => raycast(this.level, this.player, Math.cos(b), Math.sin(b)).distance - raycast(this.level, this.player, Math.cos(a), Math.sin(a)).distance)[0];
    this.pickups = (level.supplies ?? []).map(supply => ({ ...supply, life: 3600 }));
    this.difficulty = difficulty;
    this.random = seededRandom(seed);
    this.flow = flowField(this.level, this.player);
    this.explored = new Uint8Array(level.data.length);
    this.spawnWave();
    this.reveal();
  }
  private get difficultyScale() { return { easy: .75, normal: 1, hard: 1.3 }[this.difficulty]; }
  private reveal() {
    const px = Math.floor(this.player.x), py = Math.floor(this.player.y);
    for (let y = py - 4; y <= py + 4; y++) for (let x = px - 4; x <= px + 4; x++) {
      if (x >= 0 && y >= 0 && x < this.level.width && y < this.level.height) this.explored[y * this.level.width + x] = 1;
    }
  }
  private spawnWave() {
    this.flow = flowField(this.level, this.player);
    const positions: Vec[] = [];
    for (let i = 0; i < this.flow.length; i++) {
      const x = i % this.level.width + .5, y = Math.floor(i / this.level.width) + .5;
      if (this.flow[i] >= 0 && Math.hypot(x - this.player.x, y - this.player.y) > 4 && canOccupy(this.level, x, y, .4)) positions.push({ x, y });
    }
    // Prefer distant cells, but small rooms must not create empty-wave loops.
    if (!positions.length) for (let i = 0; i < this.flow.length; i++) {
      const x = i % this.level.width + .5, y = Math.floor(i / this.level.width) + .5;
      if (this.flow[i] > 1 && canOccupy(this.level, x, y, .4)) positions.push({ x, y });
    }
    // Authored compositions alternate pressure, ranged control and heavier escorts.
    // Spawn locations stay seeded, while each wave has a readable tactical identity.
    const bossWave = this.wave % 5 === 0;
    const patterns: { name: string; kinds: EnemyKind[]; density: number }[] = [
      { name: 'Premier contact', kinds: ['grunt', 'grunt', 'grunt'], density: 1 },
      { name: 'Assaut éclair', kinds: ['runner', 'runner', 'grunt'], density: 1.08 },
      { name: 'Tirs croisés', kinds: ['spitter', 'grunt', 'spitter', 'runner'], density: .9 },
      { name: 'Escorte blindée', kinds: ['brute', 'grunt', 'runner', 'grunt'], density: .85 },
    ];
    const pattern = patterns[(this.wave - 1) % 5] ?? patterns[0];
    this.waveLabel = bossWave ? 'Rupture — unité lourde' : this.wave > 5 && pattern === patterns[0] ? 'Contre-offensive' : pattern.name;
    const count = Math.min(24, Math.ceil((3 + this.wave * 1.35) * this.difficultyScale * (bossWave ? .8 : pattern.density)), positions.length);
    for (let i = 0; i < count; i++) {
      // Start the first encounter promptly, keeping the four-tile safety margin.
      const nearby = this.wave === 1 && i < 2 ? positions.filter(p => this.flow[Math.floor(p.y) * this.level.width + Math.floor(p.x)] <= 12) : [];
      const candidates = nearby.length ? nearby : positions;
      const choice = candidates[Math.floor(this.random() * candidates.length)];
      const position = positions.splice(positions.indexOf(choice), 1)[0];
      const kind: EnemyKind = bossWave ? i === 0 ? 'boss' : ['grunt', 'runner', 'spitter'][i % 3] as EnemyKind : pattern.kinds[i % pattern.kinds.length];
      const stats = ENEMY_STATS[kind];
      const hp = Math.round(stats.hp * (1 + .055 * (this.wave - 1)));
      this.enemies.push({ ...position, id: this.nextId++, kind, hp, maxHp: hp, radius: stats.radius, speed: stats.speed * Math.min(1.65, 1 + this.wave * .025), attack: 1 + this.random(), flash: 0 });
    }
    if (!count) throw new Error('La zone de départ est trop petite pour une vague.');
    this.events.push(this.wave % 5 === 0 ? 'boss' : 'wave');
  }
  selectWeapon(id: WeaponId) { if (id !== this.weapon) this.gatlingSpin = 0; this.weapon = id; }
  private impact(x: number, y: number, kind: Impact['kind']) {
    const maxLife = kind === 'hit' ? .23 : .32;
    this.impacts.push({ x, y, kind, life: maxLife, maxLife });
    if (this.impacts.length > 64) this.impacts.splice(0, this.impacts.length - 64);
  }
  shoot(): boolean {
    if (!['playing', 'intermission'].includes(this.phase) || this.cooldown > 0) return false;
    if (this.weapon !== 'pistol' && this.ammo[this.weapon] <= 0) {
      this.weapon = 'pistol'; this.gatlingSpin = 0; this.events.push('empty'); return false;
    }
    const w = WEAPONS[this.weapon];
    if (this.weapon !== 'pistol') this.ammo[this.weapon]--;
    this.cooldown = this.weapon === 'gatling' ? .22 - this.gatlingSpin * (.22 - w.cooldown) : w.cooldown;
    this.recoil = this.weapon === 'shotgun' ? 1.25 : this.weapon === 'gatling' ? .5 : .8; this.shots++;
    let connected = false;
    const pushed = new Set<Enemy>();
    for (let i = 0; i < w.pellets; i++) {
      const angle = this.player.angle + (this.random() - .5) * w.spread;
      const enemy = chooseHit(this.level, this.player, angle, this.enemies, w.range);
      if (!enemy) {
        const wall = raycast(this.level, this.player, Math.cos(angle), Math.sin(angle));
        if (wall.distance <= w.range) this.impact(this.player.x + Math.cos(angle) * (wall.distance - .025), this.player.y + Math.sin(angle) * (wall.distance - .025), 'wall');
        continue;
      }
      connected = true; enemy.hp -= w.damage * this.damageBonus * ((enemy.vulnerable ?? 0) > 0 ? 1.6 : 1); enemy.flash = .12;
      this.impact(enemy.x, enemy.y, 'hit');
      if (this.weapon === 'shotgun') pushed.add(enemy);
      if (enemy.hp <= 0) {
        this.kills++; this.killMarker = .22; this.events.push('kill');
        this.corpses.push({ x: enemy.x, y: enemy.y, kind: enemy.kind, life: 1.8, maxLife: 1.8 });
        if (this.corpses.length > 32) this.corpses.shift();
        if (this.kills % 3 === 0 || enemy.kind === 'boss') {
          const kind = enemy.kind === 'boss' ? 'armor' : this.kills % 6 === 0 ? 'health' : 'ammo';
          this.pickups.push({ x: enemy.x, y: enemy.y, kind, life: 40 });
        }
      }
    }
    for (const enemy of pushed) {
      if (enemy.hp <= 0) continue;
      const distance = Math.hypot(enemy.x - this.player.x, enemy.y - this.player.y);
      if (distance < .001) continue;
      const force = enemy.kind === 'boss' ? .12 : enemy.kind === 'brute' ? .3 : .65;
      move(this.level, enemy, (enemy.x - this.player.x) / distance * force, (enemy.y - this.player.y) / distance * force, enemy.radius);
      // A close shotgun blast interrupts ordinary enemies; bosses retain their warning.
      if (enemy.kind !== 'boss') { enemy.telegraph = 0; enemy.attack = Math.max(enemy.attack, .55); }
    }
    if (connected) { this.hits++; this.hitMarker = .13; }
    this.events.push(this.weapon);
    return true;
  }
  private announceAttack(enemy: Enemy, kind: NonNullable<Enemy['attackKind']>, duration: number, angle: number) {
    enemy.attackKind = kind; enemy.telegraph = duration; enemy.telegraphMax = duration; enemy.aimAngle = angle;
    this.events.push(kind === 'charge' ? 'charge-warning' : kind === 'shot' ? 'shot-warning' : 'melee-warning');
  }
  private finishAttack(enemy: Enemy) {
    const angle = enemy.aimAngle ?? 0;
    if (enemy.attackKind === 'charge') { enemy.charging = .85; this.events.push('charge'); return; }
    if (enemy.attackKind === 'shot') {
      const spread = enemy.kind === 'boss' ? [-.32, -.16, 0, .16, .32] : [0];
      for (const offset of spread) {
        const direction = angle + offset;
        this.bullets.push({ x: enemy.x, y: enemy.y, vx: Math.cos(direction) * 3.4, vy: Math.sin(direction) * 3.4, life: 5 });
      }
      if (this.bullets.length > 128) this.bullets.splice(0, this.bullets.length - 128);
      enemy.attack = enemy.kind === 'boss' ? 2 : 2.2;
      if (enemy.kind === 'boss') enemy.vulnerable = 1.2;
      this.events.push('enemy-shot'); return;
    }
    const ex = this.player.x - enemy.x, ey = this.player.y - enemy.y, distance = Math.hypot(ex, ey);
    const facing = distance < .001 || (ex * Math.cos(angle) + ey * Math.sin(angle)) / distance > .45;
    const clear = distance < .001 || raycast(this.level, enemy, ex / distance, ey / distance).distance >= distance - .01;
    if (distance < .92 && facing && clear) this.damage((enemy.kind === 'boss' ? 28 : enemy.kind === 'brute' ? 20 : 10) * this.difficultyScale);
    enemy.attack = enemy.kind === 'runner' ? .65 : .9;
    if (enemy.kind === 'boss') { enemy.vulnerable = .8; enemy.attack = 1.4; }
    this.events.push('melee');
  }
  private damage(amount: number) {
    if (this.phase === 'dead') return;
    const absorbed = Math.min(this.player.armor, amount * .6);
    this.player.armor -= absorbed;
    this.player.health = Math.max(0, this.player.health - amount + absorbed);
    this.hurt = .3; this.events.push('hurt');
    if (this.player.health <= 0) { this.phase = 'dead'; this.events.push('dead'); }
  }
  upgrade(kind: 'health' | 'damage' | 'speed') {
    if (this.phase !== 'upgrade') return;
    if (kind === 'health') { this.player.health = 100; this.player.armor = Math.min(100, this.player.armor + 40); }
    if (kind === 'damage') this.damageBonus = Math.min(2, this.damageBonus + .15);
    if (kind === 'speed') this.speedBonus = Math.min(1.4, this.speedBonus + .08);
    this.phase = 'intermission'; this.countdown = 3; this.events.push('pickup');
  }
  update(dt: number, input: Controls) {
    if (this.phase === 'dead' || this.phase === 'upgrade') return;
    dt = clamp(dt, 0, .05); this.time += dt;
    this.cooldown = Math.max(0, this.cooldown - dt);
    this.recoil = Math.max(0, this.recoil - dt * 6);
    this.hurt = Math.max(0, this.hurt - dt);
    this.hitMarker = Math.max(0, this.hitMarker - dt);
    this.killMarker = Math.max(0, this.killMarker - dt);
    this.gatlingSpin = this.weapon === 'gatling' ? clamp(this.gatlingSpin + (input.fire ? dt / .8 : -dt / .35), 0, 1) : 0;
    this.impacts = this.impacts.filter(effect => (effect.life -= dt) > 0);
    this.corpses = this.corpses.filter(effect => (effect.life -= dt) > 0);
    this.player.angle += input.turn * dt * 2.3;
    const magnitude = Math.max(1, Math.hypot(input.forward, input.strafe));
    const moving = input.forward !== 0 || input.strafe !== 0;
    const sprint = input.sprint && moving && this.player.stamina > 1;
    this.player.stamina = clamp(this.player.stamina + (sprint ? -30 : 22) * dt, 0, 100);
    const speed = 2.8 * this.speedBonus * (sprint ? 1.65 : 1) * dt;
    const dx = Math.cos(this.player.angle), dy = Math.sin(this.player.angle);
    move(this.level, this.player, (dx * input.forward - dy * input.strafe) / magnitude * speed, (dy * input.forward + dx * input.strafe) / magnitude * speed);
    let opened = false;
    for (let y = Math.floor(this.player.y) - 1; y <= Math.floor(this.player.y) + 1; y++) for (let x = Math.floor(this.player.x) - 1; x <= Math.floor(this.player.x) + 1; x++) {
      if (tile(this.level, x, y) === 3 && Math.hypot(x + .5 - this.player.x, y + .5 - this.player.y) < 1.5) { this.level.data[y * this.level.width + x] = 0; opened = true; }
    }
    if (opened) { this.events.push('door'); this.flowTimer = 0; }
    this.reveal();
    if (input.fire) this.shoot();
    this.flowTimer -= dt;
    if (this.flowTimer <= 0) { this.flow = flowField(this.level, this.player); this.flowTimer = .35; }
    for (const enemy of this.enemies) {
      if (enemy.hp <= 0) continue;
      enemy.flash = Math.max(0, enemy.flash - dt); enemy.attack -= dt;
      enemy.vulnerable = Math.max(0, (enemy.vulnerable ?? 0) - dt);
      if ((enemy.charging ?? 0) > 0) {
        const angle = enemy.aimAngle ?? 0, previous = { x: enemy.x, y: enemy.y };
        const chargeStep = 5.5 * dt;
        move(this.level, enemy, Math.cos(angle) * chargeStep, Math.sin(angle) * chargeStep, enemy.radius);
        enemy.charging = Math.max(0, enemy.charging! - dt);
        // Resolve contact along the whole travelled segment, including slow frames.
        const sx = enemy.x - previous.x, sy = enemy.y - previous.y;
        const lengthSquared = sx * sx + sy * sy;
        const closest = lengthSquared > 0 ? clamp(((this.player.x - previous.x) * sx + (this.player.y - previous.y) * sy) / lengthSquared, 0, 1) : 0;
        const contact = Math.hypot(this.player.x - previous.x - sx * closest, this.player.y - previous.y - sy * closest) < enemy.radius + .22;
        if (contact) { this.damage(25 * this.difficultyScale); enemy.charging = 0; }
        if (Math.hypot(sx, sy) < chargeStep * .7) enemy.charging = 0;
        if (enemy.charging <= 0) { enemy.vulnerable = 1.5; enemy.attack = 2; this.events.push('boss-recovery'); }
        if (this.player.health <= 0) return;
        continue;
      }
      if ((enemy.telegraph ?? 0) > 0) {
        enemy.telegraph = Math.max(0, enemy.telegraph! - dt);
        if (enemy.telegraph <= 0) this.finishAttack(enemy);
        if (this.player.health <= 0) return;
        continue;
      }
      if (enemy.vulnerable > 0) continue;
      const ex = this.player.x - enemy.x, ey = this.player.y - enemy.y, distance = Math.hypot(ex, ey);
      const clear = distance < .001 || raycast(this.level, enemy, ex / distance, ey / distance).distance >= distance - .01;
      if (clear && enemy.attack <= 0) {
        const angle = Math.atan2(ey, ex);
        if (distance < .85) {
          this.announceAttack(enemy, 'melee', enemy.kind === 'runner' ? .38 : enemy.kind === 'brute' || enemy.kind === 'boss' ? .7 : .52, angle);
          continue;
        }
        if (enemy.kind === 'boss' && distance > 1.5 && distance < 8 && enemy.attackKind !== 'charge') {
          this.announceAttack(enemy, 'charge', .9, angle); continue;
        }
        if ((enemy.kind === 'spitter' || enemy.kind === 'boss') && distance > 1.5 && distance < 12) {
          this.announceAttack(enemy, 'shot', enemy.kind === 'boss' ? .85 : .65, angle); continue;
        }
      }
      let tx = this.player.x, ty = this.player.y;
      if (!clear || !clearPassage(this.level, enemy, this.player, enemy.radius)) {
        const x = Math.floor(enemy.x), y = Math.floor(enemy.y);
        let best = this.flow[y * this.level.width + x];
        // Re-centre in the current cell if the next turn is not yet wide enough.
        // Switching to a new cell centre immediately on crossing its boundary cuts corners.
        tx = x + .5; ty = y + .5;
        for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]]) {
          if (tile(this.level, nx, ny) !== 0) continue;
          const cost = this.flow[ny * this.level.width + nx];
          if (cost >= 0 && (best < 0 || cost < best) && clearPassage(this.level, enemy, { x: nx + .5, y: ny + .5 }, enemy.radius)) { best = cost; tx = nx + .5; ty = ny + .5; }
        }
      }
      const length = Math.hypot(tx - enemy.x, ty - enemy.y);
      if (distance > .5 && length > .02 && !(enemy.kind === 'spitter' && clear && distance > 2 && distance < 5)) move(this.level, enemy, (tx - enemy.x) / length * enemy.speed * dt, (ty - enemy.y) / length * enemy.speed * dt, enemy.radius);
      for (const other of this.enemies) {
        if (other.id >= enemy.id || other.hp <= 0) continue;
        const sx = enemy.x - other.x, sy = enemy.y - other.y, separation = Math.hypot(sx, sy);
        if (separation > .001 && separation < .65) move(this.level, enemy, sx / separation * dt * .35, sy / separation * dt * .35, enemy.radius);
      }
    }
    for (const bullet of this.bullets) {
      bullet.life -= dt;
      const steps = Math.max(1, Math.ceil(Math.hypot(bullet.vx, bullet.vy) * dt / .1));
      for (let step = 0; step < steps && bullet.life > 0; step++) {
        const nextX = bullet.x + bullet.vx * dt / steps, nextY = bullet.y + bullet.vy * dt / steps;
        if (tile(this.level, nextX, nextY) !== 0) { this.impact(bullet.x, bullet.y, 'wall'); bullet.life = 0; break; }
        bullet.x = nextX; bullet.y = nextY;
        if (Math.hypot(bullet.x - this.player.x, bullet.y - this.player.y) < .3) { bullet.life = 0; this.damage(14 * this.difficultyScale); }
      }
      if (this.player.health <= 0) return;
    }
    this.bullets = this.bullets.filter(b => b.life > 0);
    this.pickups = this.pickups.filter(p => {
      p.life -= dt;
      if (Math.hypot(p.x - this.player.x, p.y - this.player.y) < .65) {
        if (p.kind === 'health' && this.player.health >= 100 || p.kind === 'armor' && this.player.armor >= 100) return p.life > 0;
        if (p.kind === 'health') this.player.health = Math.min(100, this.player.health + 30);
        if (p.kind === 'armor') this.player.armor = Math.min(100, this.player.armor + 35);
        if (p.kind === 'ammo') { this.ammo.shotgun = Math.min(60, this.ammo.shotgun + 6); this.ammo.gatling = Math.min(300, this.ammo.gatling + 40); }
        this.events.push('pickup'); return false;
      }
      return p.life > 0;
    });
    // Keep only living enemies: long runs must not accumulate corpses indefinitely.
    this.enemies = this.enemies.filter(e => e.hp > 0);
    if (this.player.health <= 0) return;
    if (!this.enemies.length && this.phase === 'playing') {
      this.player.health = Math.min(100, this.player.health + 8);
      this.ammo.shotgun = Math.min(60, this.ammo.shotgun + 4); this.ammo.gatling = Math.min(300, this.ammo.gatling + 25);
      this.bullets = [];
      this.phase = this.wave % 3 === 0 ? 'upgrade' : 'intermission'; this.countdown = 4; this.events.push('clear');
    } else if (this.phase === 'intermission') {
      this.countdown -= dt;
      if (this.countdown <= 0) { this.wave++; this.phase = 'playing'; this.spawnWave(); }
    }
  }
}
